
import React, { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { ModalCampaign, ModalCampaignQuestion } from '@/types/modalCampaigns';
import ModalDesktopVariant from './components/ModalDesktopVariant';
import ModalMobileVariant from './components/ModalMobileVariant';

interface CampaignModalProps {
  campaign: ModalCampaign | null;
  questions: ModalCampaignQuestion[];
  open: boolean;
  userAnswers: Record<string, string[]>;
  quizSubmitted: boolean;
  quizPassed: boolean;
  score: number;
  attemptNumber: number;
  canDismiss: boolean;
  onDismiss: (permanently?: boolean) => void;
  onAnswer: (questionId: string, selectedOptions: string[]) => void;
  onSubmit: () => void;
  onReset: () => void;
  currentQuestionIndex: number;
  onPrevQuestion: () => void;
  onNextQuestion: () => void;
}

const CampaignModal: React.FC<CampaignModalProps> = ({
  campaign,
  questions,
  open,
  userAnswers,
  quizSubmitted,
  quizPassed,
  score,
  attemptNumber,
  canDismiss,
  onDismiss,
  onAnswer,
  onSubmit,
  onReset,
  currentQuestionIndex,
  onPrevQuestion,
  onNextQuestion
}) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    // Fix for body scrolling issues
    if (open) {
      document.body.classList.add('has-modal-open');
    } else {
      document.body.classList.remove('has-modal-open');
    }
    
    return () => {
      document.body.classList.remove('has-modal-open');
    };
  }, [open]);

  if (!campaign) return null;
  
  // This function handles the Dialog/Drawer onOpenChange event
  const handleOpenChange = (open: boolean) => {
    // Only call onDismiss if the modal is being closed AND user is allowed to dismiss
    if (!open && canDismiss) {
      onDismiss();
    }
    // If trying to close but not allowed, prevent by doing nothing
  };
  
  // Use Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <ModalMobileVariant
        campaign={campaign}
        questions={questions}
        open={open}
        userAnswers={userAnswers}
        quizSubmitted={quizSubmitted}
        quizPassed={quizPassed}
        score={score}
        attemptNumber={attemptNumber}
        currentQuestionIndex={currentQuestionIndex}
        canDismiss={canDismiss}
        onOpenChange={handleOpenChange}
        onDismiss={onDismiss}
        onAnswer={onAnswer}
        onSubmit={onSubmit}
        onReset={onReset}
        onPrevQuestion={onPrevQuestion}
        onNextQuestion={onNextQuestion}
      />
    );
  }
  
  return (
    <ModalDesktopVariant
      campaign={campaign}
      questions={questions}
      open={open}
      userAnswers={userAnswers}
      quizSubmitted={quizSubmitted}
      quizPassed={quizPassed}
      score={score}
      attemptNumber={attemptNumber}
      currentQuestionIndex={currentQuestionIndex}
      canDismiss={canDismiss}
      onOpenChange={handleOpenChange}
      onDismiss={onDismiss}
      onAnswer={onAnswer}
      onSubmit={onSubmit}
      onReset={onReset}
      onPrevQuestion={onPrevQuestion}
      onNextQuestion={onNextQuestion}
    />
  );
};

export default CampaignModal;
